import { RUNTIME, assertCloudConfigured } from '../../config/runtime';
import type { DepartmentStoragePolicy, DriveConnection, StorageQuota } from '../../types';
import { apiRequest } from '../api';

export interface DriveUploadResult {
  fileId: string;
  webViewLink?: string;
  size: number;
  mimeType: string;
  name: string;
  connectorId: string;
  folderId?: string;
  storagePolicy: DepartmentStoragePolicy['mode'];
  selectionReason: string;
}

export interface DriveConnectionStatus {
  departmentId: string;
  email?: string;
  displayName?: string;
  folderId?: string;
  connected: boolean;
  disabled?: boolean;
  status?: 'connected' | 'disabled' | 'disconnected';
  connectedAt?: string;
  lastCheckedAt?: string;
  lastSyncedAt?: string;
  lastError?: string;
  disabledAt?: string;
  disabledBy?: string;
  connectorId?: string;
  connectors?: DriveConnection[];
  aggregate?: { totalBytes: number; usedBytes: number; availableBytes: number; unavailableBytes: number };
  policy?: DepartmentStoragePolicy;
}

export interface DriveTestResult {
  ok: boolean;
  checkedAt: string;
  email?: string;
  displayName?: string;
  usedBytes?: number;
  totalBytes?: number;
  error?: string;
}

export function getDriveConnectUrl(departmentId: string, connectorId?: string): string {
  assertCloudConfigured();
  const connector = connectorId ? `&connectorId=${encodeURIComponent(connectorId)}` : '';
  return `${RUNTIME.apiBaseUrl}/api/google/oauth/start?departmentId=${encodeURIComponent(departmentId)}${connector}`;
}

export async function getDriveStatus(departmentId: string): Promise<DriveConnectionStatus> {
  return apiRequest(`/api/drive/status?departmentId=${encodeURIComponent(departmentId)}`);
}

export async function getDriveQuota(departmentId: string): Promise<StorageQuota> {
  return apiRequest(`/api/drive/quota?departmentId=${encodeURIComponent(departmentId)}`);
}

export async function uploadToDrive(
  departmentId: string,
  file: Blob,
  fileName: string,
  mimeType: string,
  existingFileId?: string,
  options?: { connectorId?: string; category?: string; archival?: boolean },
): Promise<DriveUploadResult> {
  const form = new FormData();
  form.set('departmentId', departmentId);
  form.set('fileName', fileName);
  form.set('mimeType', mimeType);
  if (existingFileId) form.set('existingFileId', existingFileId);
  if (options?.connectorId) form.set('connectorId', options.connectorId);
  if (options?.category) form.set('category', options.category);
  if (options?.archival) form.set('archival', 'true');
  form.set('file', file, fileName);
  return apiRequest('/api/drive/upload', { method: 'POST', body: form });
}


export async function deleteDriveFile(departmentId: string, fileId: string, connectorId?: string): Promise<void> {
  await apiRequest('/api/drive/file', {
    method: 'DELETE',
    body: JSON.stringify({ departmentId, fileId, connectorId }),
  });
}

export async function disconnectDrive(departmentId: string, connectorId?: string): Promise<void> {
  await apiRequest('/api/drive/disconnect', {
    method: 'POST',
    body: JSON.stringify({ departmentId, connectorId }),
  });
}

export async function updateDriveSettings(
  departmentId: string,
  patch: { connectorId?: string; folderId?: string; displayName?: string; priority?: number; weight?: number; isDefault?: boolean; purpose?: DriveConnection['purpose'] },
): Promise<void> {
  await apiRequest('/api/drive/settings', {
    method: 'PATCH',
    body: JSON.stringify({ departmentId, ...patch }),
  });
}

export async function disableDrive(departmentId: string, connectorId?: string): Promise<void> {
  await apiRequest('/api/drive/disable', { method: 'POST', body: JSON.stringify({ departmentId, connectorId }) });
}

export async function enableDrive(departmentId: string, connectorId?: string): Promise<void> {
  await apiRequest('/api/drive/enable', { method: 'POST', body: JSON.stringify({ departmentId, connectorId }) });
}

export async function testDriveConnection(departmentId: string, connectorId?: string): Promise<DriveTestResult> {
  return apiRequest('/api/drive/test', { method: 'POST', body: JSON.stringify({ departmentId, connectorId }) });
}

export async function getStoragePolicy(departmentId: string): Promise<DepartmentStoragePolicy> {
  const response = await apiRequest<{ policy: DepartmentStoragePolicy }>(`/api/drive/policy?departmentId=${encodeURIComponent(departmentId)}`);
  return response.policy;
}

export async function updateStoragePolicy(departmentId: string, policy: DepartmentStoragePolicy): Promise<DepartmentStoragePolicy> {
  const response = await apiRequest<{ policy: DepartmentStoragePolicy }>('/api/drive/policy', { method: 'PATCH', body: JSON.stringify({ departmentId, ...policy }) });
  return response.policy;
}

export async function previewDriveSelection(input: { departmentId: string; bytes: number; category?: string; connectorId?: string; archival?: boolean }): Promise<{ connector: { id: string; email?: string; displayName?: string; availableBytes: number; isDefault: boolean; status: string } | null; policyUsed: DepartmentStoragePolicy['mode']; selectionReason: string }> {
  const params = new URLSearchParams({ departmentId: input.departmentId, bytes: String(input.bytes) });
  if (input.category) params.set('category', input.category);
  if (input.connectorId) params.set('connectorId', input.connectorId);
  if (input.archival) params.set('archival', 'true');
  return apiRequest(`/api/drive/selection?${params}`);
}
